import React, { useState, useEffect } from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

// Icon components for modals
const BoxIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
  </svg>
);

const BarcodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
    <path d="M6 8h1v4H6V8zm2 0h1v4H8V8zm2 0h1v4h-1V8zm2 0h1v4h-1V8zm2 0h1v4h-1V8z" />
  </svg>
);

const API_URL = '/api';

// Batch Info Modal Component
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BoxIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? new Date(batch.ExpiryDate || batch.expiryDate).toLocaleDateString() 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BoxIcon className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Info Modal Component
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BarcodeIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {serial.SerialNumber || serial || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BarcodeIcon className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReceiptViewModal = ({ open, onClose, receiptId }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department-related state
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the ReceiptFromProduction screen configuration
      const receiptScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'ReceiptFromProduction'.toLowerCase()
      );
      
      console.log('ReceiptFromProduction screen configuration:', receiptScreen);
      if (receiptScreen) {
        setScreenConfig(receiptScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = receiptScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = receiptScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };

  useEffect(() => {
    if (open && receiptId) {
      fetchScreenConfig();
      fetchReceiptDetails();
    }
  }, [open, receiptId]);

  const fetchReceiptDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/receipt-from-production/${receiptId.ReceiptID || receiptId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch receipt details');
      
      const data = await response.json();

      console.log('Fetched receipt details:', data);
      setReceipt(data);
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      alert('Error loading receipt details');
    } finally {
      setLoading(false);
    }
  };

  // Functions to open batch and serial modals
  const openBatchModal = (batches, productName) => {
    setSelectedBatches(batches);
    setSelectedProductName(productName);
    setShowBatchModal(true);
  };

  const openSerialModal = (serials, productName, location) => {
    // Extract just the serial numbers from the objects
    const serialNumbers = serials.map(serial => 
      typeof serial === 'object' && serial.SerialNumber ? serial.SerialNumber : serial
    );
    
    setSelectedSerials(serialNumbers);
    setSelectedProductName(productName);
    setSelectedLocation(location);
    setShowSerialModal(true);
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Receipt from Production Details</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
                <p>Loading receipt details...</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          {receipt ? (
            <div className="space-y-6 pb-6">
              {/* Receipt Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Receipt Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receipt ID</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.ReceiptID || ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receipt Date</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.ReceiptDate ? new Date(receipt.ReceiptDate).toLocaleDateString() : ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Production Order ID</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.ProductionOrderID || ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Issued Quantity (from Issue Table)</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.ProductionOrderQty || ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Received By User ID</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.ReceivedByUserID || ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Received By User Name</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                      {receipt.ReceivedByUserName || ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {receipt.BranchCode ? `${receipt.BranchCode} - ${receipt.BranchName || ''}` : ''}
                    </div>
                  </div>
                  
                  {/* Department Field - Only shown if ShowOnHeader is true */}
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Department</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {receipt.DepartmentID ? `${receipt.DepartmentCode} - ${receipt.DepartmentName || ''}` : '-'}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        receipt.Status === 'ACTIVE' || receipt.Status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {receipt.Status === 'ACTIVE' || receipt.Status === 1 ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Add to Inventory</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        receipt.InventoryFlag === true || receipt.InventoryFlag === 1 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {receipt.InventoryFlag === true || receipt.InventoryFlag === 1 || receipt.InventoryFlag === '1' ? 'YES' : 'NO'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Receipt Items */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Receipt Items</h3>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Material Type</th>
                        <th className="p-2">Product Code</th>
                        <th className="p-2">Product Name</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Quantity</th>
                        <th className="p-2">Calculated Qty</th>
                        <th className="p-2">Rate</th>
                        <th className="p-2">Value</th>
                        <th className="p-2">Warehouse</th>
                        {/* Department column - Only shown if ShowOnItem is true */}
                        {showDepartmentItemField && <th className="p-2">Department</th>}
                        <th className="p-2">Tracking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.Details && receipt.Details.length > 0 ? (
                        receipt.Details.map((item, idx) => (
                          <tr key={idx} className="border-t border-[#E2E8F0]">
                            <td className="p-2 text-center">{idx + 1}</td>
                            <td className="p-2">
                              <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.MaterialType || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                {item.ProductCode || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                {item.ProductName || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                {item.UOM || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.Qty || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.CalculatedQty || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.Rate || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.Value || ''}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.Warehouse || ''}
                              </div>
                            </td>
                            {/* Department cell - Only shown if ShowOnItem is true */}
                            {showDepartmentItemField && (
                              <td className="p-2">
                                <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.DepartmentID ? `${item.DepartmentCode || ''}` : '-'}
                                </div>
                              </td>
                            )}
                            <td className="p-2">
                              {item.Batches && item.Batches.length > 0 ? (
                                <button 
                                  onClick={() => openBatchModal(item.Batches, item.ProductName)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  View Batches ({item.Batches.length})
                                </button>
                              ) : item.SerialNumbers && item.SerialNumbers.length > 0 ? (
                                <button 
                                  onClick={() => openSerialModal(item.SerialNumbers, item.ProductName, item.Warehouse)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  View Serials ({item.SerialNumbers.length})
                                </button>
                              ) : (
                                <span>N/A</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={showDepartmentItemField ? 12 : 11} className="p-4 text-center text-gray-500">
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Total Summary Section */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Quantity</span>
                      <span className="text-base font-semibold">
                        {receipt.Details && receipt.Details.length > 0 
                          ? receipt.Details.reduce((sum, item) => sum + (parseFloat(item.Qty) || 0), 0).toFixed(2)
                          : '0.00'}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Value</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {receipt.Details && receipt.Details.length > 0 
                          ? receipt.Details.reduce((sum, item) => sum + (parseFloat(item.Value) || 0), 0).toFixed(2)
                          : '0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Receipt details not found</p>
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Batch Info Modal */}
      <BatchInfoModal 
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        batches={selectedBatches}
        productName={selectedProductName}
      />
      
      {/* Serial Info Modal */}
      <SerialInfoModal 
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        serialNumbers={selectedSerials}
        productName={selectedProductName}
        locationCode={selectedLocation}
      />
    </div>
  );
};

export default ReceiptViewModal;